import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { Chip } from '@pets-ui/Chip/Chip'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useMemo, useState } from 'react'
import { CouponProductSelectorItemVariationListProductItem } from './CouponProductSelectorItemVariationListProductItem'

const styles = stylex.create({
  chip: {
    padding: '8px 12px',
  },
})

export interface CouponProductSelectorItemVariationListProductsProps {
  products: ECommerceProduct[]
}

export const CouponProductSelectorItemVariationListProducts = (props: CouponProductSelectorItemVariationListProductsProps) => {
  const { products } = props
  const [selectedFirstLevelButtonKey, setSelectedFirstLevelButtonKey] = useState<string | null>(null)
  const groupByFirstLevelButton = useMemo(() =>
    products.reduce<Record<string, ECommerceProduct[]>>((acc, product) => {
      const firstLevelButton = product.first_level_button
      if (firstLevelButton) {
        acc[firstLevelButton] = [...(acc[firstLevelButton] || []), product]
      }
      else {
        acc['其他'] = [...(acc['其他'] || []), product]
      }
      return acc
    }, {}), [products])

  const firstLevelButtonKeys = useMemo(() => Object.keys(groupByFirstLevelButton), [groupByFirstLevelButton])
  const filteredProducts = useMemo(() => {
    if (selectedFirstLevelButtonKey !== null) {
      return groupByFirstLevelButton[selectedFirstLevelButtonKey] || []
    }
    return products
  }, [groupByFirstLevelButton, products, selectedFirstLevelButtonKey])

  useEffect(() => {
    if (selectedFirstLevelButtonKey === null && firstLevelButtonKeys[0]) {
      setSelectedFirstLevelButtonKey(firstLevelButtonKeys[0])
    }
  }, [firstLevelButtonKeys, selectedFirstLevelButtonKey])

  const handleAttributeChipClick = useCallbackEvent((attributeKey: string) => () => {
    setSelectedFirstLevelButtonKey(attributeKey)
  })

  return (
    <VStack p={2} pt={0} divider={<Divider color={colors.grey200} />}>
      {firstLevelButtonKeys.length > 1 && (
        <HStack gap={2} p={2} backgroundColor={colors.grey50}>
          {firstLevelButtonKeys.map(attributeKey => (
            <Chip
              rootStyle={styles.chip}
              key={attributeKey}
              selected={selectedFirstLevelButtonKey === attributeKey}
              onClick={handleAttributeChipClick(attributeKey)}
              color="primary"
              variant="square"
              isOutlined
            >
              {attributeKey}
            </Chip>
          ))}
        </HStack>
      )}
      {filteredProducts.map(product => (
        <CouponProductSelectorItemVariationListProductItem
          key={product.product_id}
          product={product}
        />
      ))}
    </VStack>
  )
}
